import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Download, Share } from 'lucide-react';
import { Button } from './ui/button';

const InstallPrompt = ({ session }) => {
    const location = useLocation();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already dismissed the prompt
        if (localStorage.getItem('installPromptDismissed')) {
            return;
        }

        // Check for iOS
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIosDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isAndroid = /Android/.test(userAgent);

        // Check if already in standalone mode
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

        if (!isStandalone && (isIosDevice || isAndroid || window.innerWidth < 768)) {
            setIsMobile(true);
            if (isIosDevice) setIsIOS(true);
            setIsVisible(true);
        }

        // Capture the event for Android/Chrome
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            if (!isStandalone) {
                setIsVisible(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            console.log(`User response to the install prompt: ${outcome}`);
            setDeferredPrompt(null);
            setIsVisible(false);
        }
    };

    const handleDismiss = () => {
        setIsVisible(false);
        localStorage.setItem('installPromptDismissed', 'true');
    };

    const isLandingPage = ['/', '/login', '/v1', '/v2', '/v3', '/privacy', '/terms'].includes(location.pathname);

    if (!isVisible || !session || isLandingPage) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
            {/* High Contrast Banner: Dark in Light mode, Light in Dark mode */}
            <div className="bg-zinc-900 dark:bg-zinc-50 border border-zinc-800 dark:border-zinc-200 p-4 rounded-xl shadow-2xl flex flex-col gap-3 relative max-w-md mx-auto text-zinc-50 dark:text-zinc-900">
                <button
                    onClick={handleDismiss}
                    className="absolute top-2 right-2 p-1 text-zinc-400 hover:text-zinc-50 dark:hover:text-zinc-900 hover:bg-white/10 dark:hover:bg-black/10 rounded-full transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-3 pr-6">
                    <div className="w-10 h-10 bg-white/10 dark:bg-black/5 rounded-lg flex items-center justify-center text-zinc-50 dark:text-zinc-900 shrink-0">
                        <Download className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-sm">Instalar Aplicativo</h4>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500">
                            {isIOS
                                ? "Adicione à tela de início para melhor experiência"
                                : "Instale o app para acesso rápido e offline"}
                        </p>
                    </div>
                </div>

                {isIOS ? (
                    <div className="text-xs text-zinc-300 dark:text-zinc-600 bg-zinc-800 dark:bg-zinc-200 p-2 rounded border border-transparent flex items-center gap-2">
                        <span>Toque em</span>
                        <Share className="w-3 h-3" />
                        <span>e selecione "Adicionar à Tela de Início"</span>
                    </div>
                ) : deferredPrompt ? (
                    <Button
                        onClick={handleInstallClick}
                        size="sm"
                        className="w-full gap-2 bg-zinc-50 text-zinc-900 hover:bg-zinc-200 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800 border-none font-semibold"
                    >
                        <Download className="w-4 h-4" />
                        Instalar Agora
                    </Button>
                ) : (
                    <div className="text-xs text-zinc-300 dark:text-zinc-600 bg-zinc-800 dark:bg-zinc-200 p-2 rounded border border-transparent">
                        Para instalar, abra o menu do navegador <span className="inline-block px-1 bg-zinc-700 dark:bg-zinc-300 rounded mx-1">⋮</span> e procure por "Instalar aplicativo".
                    </div>
                )}
            </div>
        </div>
    );
};
export default InstallPrompt;
